import { useState } from 'react'

import { formatClock } from '@krp/client-runtime'
import { Button, SelectField, TextField } from '@krp/ui-kit'

import { ATOL_DRIVER_ID } from '../model/drivers'
import { scanVm, type CandidateVm } from '../model/scan'
import type { DriverCategory, EquipmentSnapshot, HardwareActions, HardwareLinks, HardwareTabId } from '../model/types'
import { Mark, type MarkTone } from './Mark'
import { blockReason, type TabProps } from './tabSupport'

const RUN_MARK: Record<string, { glyph: string; tone: MarkTone }> = {
  running: { glyph: '…', tone: 'wait' },
  done: { glyph: '✓', tone: 'ok' },
  failed: { glyph: '✕', tone: 'block' },
  cancelled: { glyph: '–', tone: 'wait' },
  idle: { glyph: '–', tone: 'wait' },
}

const CANDIDATE_TONE: Record<string, MarkTone> = { '✓': 'ok', '!': 'warn', '✕': 'block', '–': 'wait' }

function CandidateItem({
  candidate,
  links,
  actions,
  locked,
  busy,
  scanDisabled,
  onOpenTab,
}: {
  candidate: CandidateVm
  links: HardwareLinks
  actions: HardwareActions
  locked: boolean
  busy: boolean
  scanDisabled: string | null
  onOpenTab: (tab: HardwareTabId) => void
}) {
  const terminal = candidate.category === 'payment-terminal'
  const atolDriver = candidate.kind === ATOL_DRIVER_ID
  // Поля черновика — память окна: пока человек не нажал «Подключить», ничего никуда не уходит.
  const [typed, setTyped] = useState<Record<string, string>>({})
  const [comFile, setComFile] = useState(candidate.suggested.comFile ?? '')
  const [confirming, setConfirming] = useState(false)

  const values: Record<string, string> = { ...candidate.suggested, ...typed }
  if (candidate.needsComPort) {
    values.port = '0'
    values.comFile = comFile.trim()
  }
  const missing = [
    ...candidate.inputs.filter((field) => (values[field.key] ?? '').trim().length === 0).map((field) => field.title),
    ...(candidate.needsComPort && comFile.trim().length === 0 ? ['имя COM-порта'] : []),
  ]
  const reason = blockReason(locked, busy) ?? (missing.length > 0 ? `Заполните: ${missing.join(', ')}` : null)
  const replace = candidate.action === 'replace'
  const connect = () => {
    actions.connect({ category: candidate.category, kind: candidate.kind, candidateId: candidate.id, values })
    setConfirming(false)
  }
  const driverLink = links.drivers[candidate.kind] ?? null

  return (
    <li className={`hw-candidate hw-box hw-box--${CANDIDATE_TONE[candidate.glyph] ?? 'wait'}`} aria-label={candidate.title}>
      <p>
        <strong>{candidate.title}</strong>
        {candidate.connection.length > 0 && <> · {candidate.connection}</>}
      </p>
      <p>
        <Mark glyph={candidate.glyph} word={candidate.word} tone={CANDIDATE_TONE[candidate.glyph] ?? 'wait'} />
        {candidate.deviceLine !== null && <> · {candidate.deviceLine}</>}
      </p>
      {candidate.text.length > 0 && <p className="hw-hint">{candidate.text}</p>}

      {candidate.inputs.length > 0 && (
        <div className="hw-form">
          {candidate.inputs.map((field) => (
            <TextField
              key={field.key}
              label={field.title}
              actionId="platform.hardware.terminal-field"
              value={values[field.key] ?? ''}
              placeholder={field.hint ?? undefined}
              onChange={(value) => setTyped((previous) => ({ ...previous, [field.key]: value }))}
            />
          ))}
        </div>
      )}
      {candidate.needsComPort && (
        <div className="hw-form">
          <TextField label="Имя COM-порта" actionId="platform.hardware.atol-com" value={comFile} placeholder="COM3" onChange={setComFile} />
        </div>
      )}

      <div className="hw-buttons">
        {(candidate.action === 'connect' || replace) &&
          !confirming &&
          (terminal ? (
            <Button
              variant="primary"
              actionId="platform.hardware.terminal-save"
              disabled={reason !== null}
              disabledReason={reason}
              onClick={connect}
            >
              Подключить
            </Button>
          ) : (
            <Button
              variant="primary"
              actionId="platform.hardware.kassa-connect"
              disabled={reason !== null}
              disabledReason={reason}
              onClick={replace ? () => setConfirming(true) : connect}
            >
              {replace ? 'Заменить кассу' : 'Подключить'}
            </Button>
          ))}
        {confirming && (
          <>
            <p role="alert" className="hw-confirm">
              Заменить кассу? Прежняя касса будет заменена этой.
            </p>
            <Button variant="danger" actionId="platform.hardware.kassa-connect" disabled={reason !== null} disabledReason={reason} onClick={connect}>
              Да, заменить
            </Button>
            <Button variant="ghost" actionId="platform.hardware.confirm-cancel" onClick={() => setConfirming(false)}>
              Не надо
            </Button>
          </>
        )}
        {candidate.action === 'rescan' && (
          <Button
            variant="secondary"
            actionId={terminal ? 'platform.hardware.terminal-scan' : 'platform.hardware.kassa-scan'}
            disabled={scanDisabled !== null}
            disabledReason={scanDisabled}
            onClick={() => actions.scan(candidate.category)}
          >
            Повторить
          </Button>
        )}
        {candidate.action === 'download-driver' && driverLink !== null && (
          <Button
            variant="primary"
            actionId={atolDriver ? 'platform.hardware.download-driver' : 'platform.hardware.terminal-driver-download'}
            onClick={() => {
              actions.downloadDriver(candidate.kind)
              onOpenTab('drivers')
            }}
          >
            Скачать драйвер
          </Button>
        )}
      </div>
    </li>
  )
}

/**
 * Блок «Сканирование» вкладки категории (п. 4): кнопка, ход по драйверам, найденное, подсказки.
 * Скан и подключение никогда не создают чек, смену или оплату.
 */
export function ScanPanel({
  snapshot,
  category,
  links,
  actions,
  locked,
  busy,
  onOpenTab,
}: TabProps & {
  snapshot: EquipmentSnapshot
  category: DriverCategory
  links: HardwareLinks
  actions: HardwareActions
  onOpenTab: (tab: HardwareTabId) => void
}) {
  const scan = scanVm(snapshot, category)
  const kassa = category === 'fiscal-register'
  const [com, setCom] = useState('')
  const scanReason = locked ? 'Нет связи с программой-драйвером' : scan.disabledReason
  const comValue = com.length > 0 ? com : (scan.comPorts[0] ?? '')

  return (
    <section aria-label="Сканирование" className="hw-scan">
      <h3 className="hw-title">Сканирование</h3>
      <div className="hw-buttons">
        <Button
          variant="primary"
          actionId={kassa ? 'platform.hardware.kassa-scan' : 'platform.hardware.terminal-scan'}
          disabled={scanReason !== null}
          disabledReason={scanReason}
          onClick={() => actions.scan(category)}
        >
          {kassa ? 'Найти кассу' : 'Найти терминал'}
        </Button>
        {scan.running && (
          <Button variant="ghost" actionId="platform.hardware.scan-cancel" onClick={() => actions.cancelScan(category)}>
            Отмена
          </Button>
        )}
        {scan.lastScanAt !== null && <span className="hw-hint">Последний раз: сегодня {formatClock(scan.lastScanAt)}</span>}
      </div>

      {scan.rows.length > 0 && (
        <ul className="hw-list" aria-label="Ход сканирования">
          {scan.rows.map((row) => {
            const mark = RUN_MARK[row.state] ?? { glyph: '–', tone: 'wait' as const }
            return (
              <li key={row.driverId}>
                <Mark glyph={mark.glyph} word={row.title} tone={mark.tone} /> — {row.text}
              </li>
            )
          })}
        </ul>
      )}

      {scan.errors.length > 0 && (
        <ul className="hw-list" aria-label="Ошибки сканирования">
          {scan.errors.map((error) => (
            <li key={error} role="alert">
              ✕ {error}
            </li>
          ))}
        </ul>
      )}

      {scan.candidates.length > 0 && (
        <ul className="hw-candidates" aria-label="Найденное оборудование">
          {scan.candidates.map((candidate) => (
            <CandidateItem
              key={`${candidate.kind}:${candidate.id}`}
              candidate={candidate}
              links={links}
              actions={actions}
              locked={locked}
              busy={busy}
              scanDisabled={scanReason}
              onOpenTab={onOpenTab}
            />
          ))}
        </ul>
      )}

      {scan.summary !== null && <p className="hw-hint">{scan.summary}</p>}
      {scan.hints.length > 0 && (
        <ul className="hw-list" aria-label="Что проверить">
          {scan.hints.map((hint) => (
            <li key={hint}>{hint}</li>
          ))}
        </ul>
      )}

      {kassa && !locked && (
        <div className="hw-com">
          <p>Касса подключена по COM?{scan.comPorts.length > 0 ? ' Порты на компьютере:' : ' Укажите порт.'}</p>
          <div className="hw-form hw-form--row">
            {scan.comPorts.length > 0 ? (
              <SelectField
                label="COM-порт"
                actionId="platform.hardware.atol-com"
                value={comValue}
                options={scan.comPorts.map((port) => ({ value: port, label: port }))}
                onChange={setCom}
              />
            ) : (
              <TextField label="COM-порт" actionId="platform.hardware.atol-com" value={com} placeholder="COM3" onChange={setCom} />
            )}
            <Button
              variant="secondary"
              actionId="platform.hardware.kassa-scan-com"
              disabled={comValue.trim().length === 0 || scanReason !== null}
              disabledReason={comValue.trim().length === 0 ? 'Выберите COM-порт' : scanReason}
              onClick={() => actions.scanComPort(comValue.trim())}
            >
              Проверить порт
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}
